import React, { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';

import { ROUTES } from '@/common';
import { DEFAULT_DATE_FORMAT, OrganizationTypes } from '@/common/constants';
import { QueryKeys, useBreadcrumbs, useHistory } from '@/modules/core/hooks';
import useOrganizationBalanceQuery from '@/modules/employer/components/BankingBalance/queries/useOrganizationBalance.query';
import useOrganizationBankingBalanceDetailsQuery from '@/modules/employer/components/BankingBalance/queries/useOrganizationBankingBalanceDetails.query';
import { useIsApplyUnmatchedPushAvailable } from '@/modules/featureFlags';
import { UnappliedFundsTab } from '@/modules/UnappliedFunds/UnappliedFunds.types';
import UnappliedFundsBanner, {
  UNAPPLIED_FUNDS_BANKING_BALANCE_PERMISSIONS,
  UnappliedFundsBannerVariant,
} from '@/modules/UnappliedFundsBanner';

import { BankingBalanceOrganizationDetails } from './components/BankingBalanceOrgDetails/BankingBalanceOrganizationDetails';

const BankingBalanceOrgDetailsContainer = () => {
  const { orgId } = useParams<{ orgId: string }>();
  const history = useHistory();
  const isUnappliedFundsAvailable = useIsApplyUnmatchedPushAvailable();
  const { formattedData, isLoading } = useOrganizationBankingBalanceDetailsQuery(orgId);
  const { logoUrl, isLoading: isLogoLoading } = useOrganizationBalanceQuery(orgId);

  const formattedLastDate = useMemo(
    () => formattedData?.lastUpdatedAt
      ? dayjs(formattedData.lastUpdatedAt).format(DEFAULT_DATE_FORMAT)
      : '-',
    [formattedData?.lastUpdatedAt]
  );

  const isPlansVisible = useMemo(
    () => formattedData?.orgType === OrganizationTypes.company
      || formattedData?.orgType === OrganizationTypes.employer,
    [formattedData?.orgType]
  );

  const handleSeeIncomingPayments = useCallback(
    () => history.push(ROUTES.UNAPPLIED_FUNDS_ORG_DETAILS(orgId), { [QueryKeys.TAB]: UnappliedFundsTab.FUNDS_HISTORY }),
    [history, orgId]
  );

  const isSubsidiary = formattedData?.orgType === OrganizationTypes.subsidiary;

  useBreadcrumbs([
    {
      route: ROUTES.BANKING_BALANCE,
      title: 'FinOps',
    },
    {
      route: ROUTES.BANKING_BALANCE_ORG_DETAILS(orgId),
      title: formattedData?.orgName || '',
    },
  ], [orgId, formattedData], { backBtn: true });

  return (
    <>
      {isSubsidiary && (
        <UnappliedFundsBanner
          variant={UnappliedFundsBannerVariant.INLINE}
          permissions={UNAPPLIED_FUNDS_BANKING_BALANCE_PERMISSIONS}
          organizationId={orgId}
        />
      )}
      <BankingBalanceOrganizationDetails
        orgId={orgId}
        formattedData={formattedData}
        logoUrl={logoUrl}
        formattedLastDate={formattedLastDate}
        isPlansVisible={isPlansVisible}
        isLoading={isLoading || isLogoLoading}
        onSeeIncomingPayments={isUnappliedFundsAvailable ? handleSeeIncomingPayments : undefined}
      />
    </>
  );
};

export default BankingBalanceOrgDetailsContainer;
